import { Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { ClosedSelectPick } from '../utils/closed-select-pick';

/**
 * Wires a `ClosedSelectPick` to the `mat-select` it settles: `(selectionChange)` with the panel
 * state, `(blur)`, the panel closing, and `(keydown)` for Enter. Those four were hand-copied into
 * three templates, each having to remember the `panelOpen` argument and the `!$event` guard; a
 * missed one brought back either a commit per arrow key or a pick that never committed
 * (MR !217 review).
 *
 * A `null` pick switches the directive off, for a host that settles only some of its selects
 * (`DefaultSelectComponent` behind `settleClosedPicks`).
 *
 * Material's own `keydown` and `blur` are host listeners of the select COMPONENT, which Angular
 * registers before any directive's on the same element, so they have run when these do -- which is
 * what `ClosedSelectPick.keydown()` relies on.
 */
@Directive({
  selector: 'mat-select[appSettleClosedPick]',
  standalone: true,
})
export class SettleClosedPickDirective<T = unknown> implements OnChanges, OnInit, OnDestroy {
  @Input('appSettleClosedPick') pick: ClosedSelectPick<T> | null | undefined;

  /** Whether Enter commits a held pick at once. See `ClosedSelectPick.keydown()`. */
  @Input() settleFlushOnEnter = true;

  private readonly subs = new Subscription();
  /** Set in `ngOnDestroy`: the deferred settle of a swapped pick must not commit into a host that is gone. */
  private destroyed = false;

  constructor(private readonly select: MatSelect, private readonly host: ElementRef<HTMLElement>) {}

  /** Capture phase: runs before Material's own keydown on the same element. See `keydownCapture()`. */
  private readonly onKeydownCapture = (event: KeyboardEvent) => this.pick?.keydownCapture(event);

  /**
   * The bound pick was swapped (`DefaultSelectComponent` turning `settleClosedPicks` off binds
   * `null`). Nothing reaches the old pick after this -- not the blur, not the panel close, not
   * `ngOnDestroy` -- so settle it here: commit what it holds, since the select has been showing
   * that value since the key press, and cancel an armed Enter, whose timer would otherwise commit
   * into a host that no longer settles picks (MR !217 review).
   *
   * The commit is a MICROTASK later, never here: `ngOnChanges` runs inside the host's change
   * detection, and a commit reaches the host's form (`onChangeCallback`, `valueChanges`) and through
   * it bindings of views already checked in this pass -- NG0100 in a dev build, and a search or save
   * fired mid-render (MR !222 review). The microtask runs once the pass and its dev-mode re-check are
   * over, inside the zone, so the commit gets a pass of its own.
   *
   * And only if the select STILL shows the held value then: a host that swaps the pick and resets or
   * patches its model in the same pass has moved the select off the walk, and committing the walk
   * afterwards would act on a value the user can no longer see -- the case `discard()` exists for
   * (MR !222 review).
   */
  ngOnChanges(changes: SimpleChanges): void {
    const previous = changes['pick']?.previousValue as ClosedSelectPick<T> | null | undefined;
    if (previous && previous !== this.pick) {
      previous.dispose();
      Promise.resolve().then(() => {
        // Swapped and destroyed in the same pass: the host is gone, and what happens to a pick held
        // at its destroy is the HOST's call (see `ClosedSelectPick.dispose`), already made in its own
        // `ngOnDestroy` (MR !222 review, round 5).
        if (this.destroyed) {
          return;
        }
        const held = previous.held;
        if (!held) {
          return;
        }
        if (this.select.compareWith(held.value, this.select.value)) {
          previous.flush();
        } else {
          previous.discard();
        }
      });
    }
  }

  ngOnInit(): void {
    this.host.nativeElement.addEventListener('keydown', this.onKeydownCapture, true);
    this.subs.add(this.select.selectionChange.subscribe((change) => this.pick?.picked(change.value, this.select.panelOpen)));
    this.subs.add(this.select.openedChange.subscribe((opened) => {
      if (!opened) {
        this.pick?.panelClosed();
      }
    }));
  }

  @HostListener('blur')
  onBlur(): void {
    this.pick?.settle(this.select.panelOpen);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    this.pick?.keydown(event, this.select, this.settleFlushOnEnter);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pick?.dispose();
    this.host.nativeElement.removeEventListener('keydown', this.onKeydownCapture, true);
    this.subs.unsubscribe();
  }
}
